<template>
  <div
    class="cxm-Input"
    :class="containerClass"
  >
    <label
      v-if="label"
      class="cxm-Input-Label"
      :for="uniqueID"
    >
      {{ label }}
      <Tooltip
        :text="infoMessage"
        position="bottom right"
        width="300px"
      >
        <Icon
          v-if="infoMessage"
          name="Info"
          size="15"
          class="cxm-Input-Info"
        />
      </Tooltip>
    </label>
    <Divider v-if="showdivider" />
    <div :class="inputContentClass">
      <slot name="leftIcons">
        <Icon
          v-if="leftIcon"
          :name="leftIcon"
          class="cxm-Input-Icon"
        />
      </slot>
      <input
        v-if="type !== 'textarea'"
        :id="uniqueID"
        ref="input"
        :name="uniqueID"
        :aria-label="uniqueID"
        data-testid="input"
        v-bind="$attrs"
        :class="inputClass"
        :value="value"
        :type="type"
        :disabled="disabled"
        :placeholder="placeholder"
        @blur="$emit('blur', $event)"
        @click="$emit('click', $event)"
        @focus="$emit('focus', $event)"
        @input="updateValue"
        @keydown="$emit('keydown', $event)"
        @keyup="$emit('keyup', $event)"
      >
      <div
        v-else
        ref="growContainer"
        :class="{ 'grow-wrap': autogrow }"
      >
        <textarea
          ref="input"
          v-bind="$attrs"
          class="cxm-Input-Base cxm-Input-Default cxm-Input-Textarea"
          :value="value"
          :disabled="disabled"
          :placeholder="placeholder"
          @input="updateValue"
          @blur="$emit('blur', $event)"
          @keydown="$emit('keydown', $event)"
          @keyup="$emit('keyup', $event)"
          @click="$emit('click', $event)"
        />
      </div>
      <slot name="rightIcons">
        <Icon
          v-if="rightIcon"
          :name="rightIcon"
          class="cxm-Input-Icon"
        />
      </slot>
    </div>
    <div
      v-show="errorMessage"
      class="cxm-Input-ErrorLabel"
    >
      {{ errorMessage }}
    </div>
  </div>
</template>

<script>
import Icon from '../Icon/Icon.vue';
import Tooltip from '../Tooltip/Tooltip.vue';
import Divider from '../Divider/Divider.vue';

export default {
  name: 'Input',
  components: { Icon, Tooltip, Divider },
  props: {
    id: {
      type: String,
      default: '',
    },
    value: {
      type: [String, Number],
      default: '',
    },
    label: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    showdivider: {
      type: Boolean,
      default: false,
    },
    type: {
      type: String,
      validator: (val) => ['text', 'number', 'textarea', 'password'].includes(val),
      default: 'text',
    },
    autogrow: {
      type: Boolean,
      default: false,
    },
    errorMessage: {
      type: String,
      default: '',
    },
    infoMessage: {
      type: String,
      default: '',
    },
    leftIcon: {
      type: String,
      default: '',
    },
    rightIcon: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      visible: false,
      uniqueID: null,
    };
  },
  computed: {
    containerClass() {
      return {
        'cxm-Input-Error': Boolean(this.errorMessage),
        'cxm-Input-Disabled': this.disabled,
      };
    },
    inputClass() {
      return {
        'cxm-Input-Base': true,
        'cxm-Input-WithIcons': this.hasIcons(),
        'cxm-Input-RightIcon': this.hasRightIcons(),
        'cxm-Input-LeftIcon': this.hasLeftIcons(),
        'cxm-Input-Default': !this.hasIcons(),
      };
    },
    inputContentClass() {
      return {
        'cxm-Input-ContentWithIcon': this.hasIcons(),
      };
    },
  },
  watch: {
    value(updatedValue) {
      this.addValueToDataset(updatedValue);
    },
  },
  mounted() {
    if (this.value) {
      this.addValueToDataset(this.value);
    }
  },
  created() {
    this.uniqueID = this.id || this._uid;
  },
  methods: {
    updateValue(e) {
      this.$emit('input', e.target.value);
      this.addValueToDataset(e.target.value);
    },
    addValueToDataset(value) {
      if (this.$refs.growContainer) {
        this.$refs.growContainer.dataset.replicatedValue = value;
      }
    },
    hasLeftIcons() {
      return Boolean(this.leftIcon || this.$slots.leftIcons);
    },
    hasRightIcons() {
      return Boolean(this.rightIcon || this.$slots.rightIcons);
    },
    hasIcons() {
      return Boolean(this.hasLeftIcons() || this.hasRightIcons());
    },
  },
};
</script>

<style scoped lang="scss">
.cxm-Input {
  position: relative;
}

.cxm-Input-ContentWithIcon {
  display: flex;
  align-items: center;
  border: var(--track-border-sm) solid var(--track-color-bluegray-50);
  border-radius: var(--track-border-radius-xs);
  padding: 0 var(--track-spacing-md);
  background: var(--track-color-white);
  flex-wrap: wrap;
}

.cxm-Input-ContentWithIcon:focus-within {
  border: var(--track-border-sm) solid var(--track-color-brand-primary);
}

.cxm-Input-Default {
  padding: calc(var(--track-spacing-sm) + var(--track-spacing-xs))
    var(--track-spacing-md);
  border: var(--track-border-sm) solid var(--track-color-bluegray-60);
  border-radius: var(--track-border-radius-xs);
  height: 40px;
  width: 100%;
}

.cxm-Input-RightIcon {
  padding-right: var(--track-spacing-sm);
}

.cxm-Input-LeftIcon {
  padding-left: var(--track-spacing-sm);
}

.cxm-Input-WithIcons {
  padding-top: calc(var(--track-spacing-sm) + var(--track-spacing-xs));
  padding-bottom: calc(var(--track-spacing-sm) + var(--track-spacing-xs));
  border: none;
  height: 38px;
  width: 100%;
  flex: 1;
}

.cxm-Input-Base {
  font-size: var(--track-font-size-xs);
  color: var(--track-color-bluegray-70);
  letter-spacing: 0.16px;
  outline: none;
  -webkit-box-sizing: border-box;
  -moz-box-sizing: border-box;
  box-sizing: border-box;
}

.cxm-Input-Default:focus {
  border: var(--track-border-sm) solid var(--track-color-brand-primary);
}

.cxm-Input-Textarea {
  resize: vertical;
  height: auto;
  font-family: var(--track-font-family-base), sans-serif;
}
.cxm-Input-Label {
  color: var(--track-color-bluegray-80);
  font-weight: var(--track-font-weight-medium);
  font-size: var(--track-font-size-xs);
  margin-bottom: var(--track-spacing-sm);
  display: flex;
  align-items: center;
  .cxm-Input-Info {
    margin-left: 5px;
    fill: var(--track-color-bluegray-70);
    display: flex;
    align-items: center;
  }
}
.cxm-Input-Error {
  .cxm-Input-Default,
  .cxm-Input-ContentWithIcon {
    border-color: var(--track-color-negative-medium);
    color: var(--track-color-negative-medium);
  }
  .cxm-Input-Label {
    color: var(--track-color-bluegray-90);
  }
}

.cxm-Input-Disabled {
  .cxm-Input-Default,
  .cxm-Input-ContentWithIcon {
    cursor: not-allowed;
    background-color: var(--track-color-bluegray-10);
    border: var(--track-border-sm) solid var(--track-color-bluegray-20);
    color: var(--track-color-bluegray-60);
    pointer-events: none;
  }
  .cxm-Input-Label {
    color: var(--track-color-bluegray-60);
  }

  svg {
    opacity: 0.5;
  }
}

.cxm-Input-ErrorLabel {
  position: absolute;
  color: var(--track-color-negative-medium);
  font-size: var(--track-font-size-xxs);
  margin-top: var(--track-spacing-xs);
}
.t-grow-wrap {
  display: grid;
}
.t-grow-wrap::after {
  content: attr(data-replicated-value) " ";
  white-space: pre-wrap;
  visibility: hidden;
}
.t-grow-wrap > textarea {
  resize: none;
  overflow: hidden;
}
.t-grow-wrap > textarea,
.t-grow-wrap::after {
  border: var(--track-border-sm) solid var(--track-color-bluegray-50);
  font: inherit;
  grid-area: 1 / 1 / 2 / 2;
  padding-bottom: 3px;
  padding-top: 3px;
  letter-spacing: inherit;
  padding-left: var(--track-spacing-md);
}

/* Chrome, Safari, Edge, Opera */
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* Firefox */
input[type="number"] {
  -moz-appearance: textfield;
}

.cxm-Input-Base::placeholder {
  /* Chrome, Firefox, Opera, Safari 10.1+ */
  color: var(--track-color-bluegray-30);
  opacity: 1; /* Firefox */
}
.cxm-Input-Base::-ms-input-placeholder {
  /* Microsoft Edge */
  color: var(--track-color-bluegray-30);
}
.cxm-Input-Icon {
  fill: var(--track-color-bluegray-70);
  &:hover {
    fill: var(--track-color-bluegray-70);
  }
}
</style>
